import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/Button';
import { FormInput } from '@/components/FormInput';
import { FormTextarea } from '@/components/FormTextarea';
import { SubPageHeader } from '@/components/shared';
import { useToast } from '@/hooks/useToast';
import { AttachmentPicker } from '../../components/AttachmentPicker';
import { useSubmitTicket } from '../../hooks/data/useDpRequestMutations';
import { withUploadedAttachments, type PendingFile } from '../../services/dpAttachmentUpload';
import { ticketFormSchema } from '../../models/ticket.schemas';
import { DP_REQUESTS_PATH } from '../../routes';
import { useAuth } from '@/contexts/AuthContext';

type TicketErrors = Partial<Record<'subject' | 'body', string>>;

export function TicketPage() {
    const navigate = useNavigate();
    const toast = useToast();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const submit = useSubmitTicket();
    const [subject, setSubject] = useState(() => searchParams.get('assunto')?.slice(0, 120) ?? '');
    const [body, setBody] = useState('');
    const [files, setFiles] = useState<PendingFile[]>([]);
    const [uploading, setUploading] = useState(false);
    const [errors, setErrors] = useState<TicketErrors>({});

    async function handleSubmit() {
        const parsed = ticketFormSchema.safeParse({ subject, body });
        if (!parsed.success) {
            const next: TicketErrors = {};
            parsed.error.issues.forEach(issue => {
                const field = issue.path[0] as keyof TicketErrors;
                next[field] = issue.message;
            });
            setErrors(next);
            toast.error('Revise o pedido', Object.values(next)[0]);
            return;
        }

        setErrors({});
        if (!user?.uid) return;
        setUploading(true);
        try {
            const requestId = await withUploadedAttachments(files, user.uid, attachments =>
                submit.mutateAsync({
                    category: 'other' as const,
                    subject: parsed.data.subject,
                    body: parsed.data.body,
                    attachments,
                })
            );
            navigate(`${DP_REQUESTS_PATH}?requestId=${requestId}`);
        } catch {
            return;
        } finally {
            setUploading(false);
        }
    }

    const busy = submit.isPending || uploading;

    return (
        <div className="mx-auto max-w-3xl space-y-6">
            <SubPageHeader
                backPlacement="end"
                backTo={DP_REQUESTS_PATH}
                icon={<ChatBubbleLeftRightIcon className="h-6 w-6" aria-hidden="true" />}
                title="Outros assuntos"
                description="Fale com o DP sobre o que precisar. Você acompanha a conversa aqui."
            />

            <div className="space-y-5 rounded-2xl border border-raro-gray-200 bg-white p-5 shadow-sm sm:p-6">
                <FormInput
                    label="Assunto"
                    name="subject"
                    value={subject}
                    onChange={event => setSubject(event.target.value)}
                    maxLength={120}
                    error={errors.subject}
                    disabled={busy}
                    required
                />
                <FormTextarea
                    label="Mensagem"
                    name="body"
                    value={body}
                    onChange={event => setBody(event.target.value)}
                    maxLength={4000}
                    showCharCount
                    rows={6}
                    error={errors.body}
                    disabled={busy}
                    required
                />
                <AttachmentPicker
                    files={files}
                    onAdd={file => setFiles(current => [...current, file])}
                    onRemove={index => setFiles(current => current.filter((_, position) => position !== index))}
                    disabled={busy}
                    hint="Opcional. PDF, JPG ou PNG. O arquivo só é enviado quando você abrir o chamado."
                />
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button variant="ghost" onClick={() => navigate(DP_REQUESTS_PATH)} disabled={busy}>
                    Cancelar
                </Button>
                <Button onClick={() => void handleSubmit()} loading={busy} disabled={!user?.uid}>
                    {uploading ? 'Enviando anexos…' : 'Abrir chamado'}
                </Button>
            </div>
        </div>
    );
}

export default TicketPage;
